import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../auth/AuthContext";
import { apiExpertQueue, apiExpertVerdict } from "../lib/api";
import { statusFor } from "../lib/status";
import { Loader2, ChevronLeft, ShieldCheck, Inbox } from "lucide-react";

const LEVELS = ["SAFE", "CAUTION", "DANGER", "LIMITED"];

export default function ExpertDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("in_review");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ risk_level: "CAUTION", summary: "", advice: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user && !["EXPERT", "ADMIN"].includes(user.role)) navigate("/");
  }, [user, navigate]);

  const load = useCallback(async (status) => {
    setData(null);
    try { setData(await apiExpertQueue(status)); } catch (_) { setData({ items: [], pending: 0 }); }
  }, []);
  useEffect(() => { if (user && ["EXPERT", "ADMIN"].includes(user.role)) load(tab); }, [user, tab, load]);

  const openItem = (it) => {
    setActive(it);
    setErr("");
    setForm({ risk_level: it.expert_risk_level || "CAUTION", summary: it.expert_summary || "", advice: it.expert_advice || "" });
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await apiExpertVerdict(active.request_id, form);
      setActive(null);
      await load("in_review");
      setTab("in_review");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("expertDash.error"));
    } finally { setBusy(false); }
  };

  if (!user || !["EXPERT", "ADMIN"].includes(user.role)) return null;

  return (
    <div className="min-h-screen pt-28 pb-24 sm:pt-32" data-testid="expert-dashboard">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#0D9488]">
            <ShieldCheck className="h-6 w-6 text-white" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("expertDash.title")}</h1>
            <p className="text-sm text-slate-500">{t("expertDash.subtitle")}</p>
          </div>
        </div>

        {active ? (
          <div className="mt-8">
            <button onClick={() => setActive(null)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-700">
              <ChevronLeft className="h-4 w-4" /> {t("expertDash.back")}
            </button>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="font-semibold text-slate-900">{t("expertDash.snapshot")}</h2>
              <p className="mt-1 text-xs text-slate-500">{t("expertDash.privacyNote")}</p>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">{t("expertDash.type")}</dt><dd className="text-slate-700">{active.input_type}</dd></div>
                <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">{t("expertDash.aiVerdict")}</dt><dd className="text-slate-700">{active.ai_risk_level || "—"} {active.ai_risk_score != null ? `(${active.ai_risk_score})` : ""}</dd></div>
                <div className="flex gap-2"><dt className="w-28 shrink-0 text-slate-500">{t("expertDash.preview")}</dt><dd className="break-words text-slate-700">{active.masked_preview || "—"}</dd></div>
              </dl>
              {active.signals_snapshot && active.signals_snapshot.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-slate-500">{t("expertDash.signals")}</p>
                  <ul className="mt-1 flex flex-wrap gap-2">
                    {active.signals_snapshot.map((s, i) => (
                      <li key={i} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">{s.label}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {active.status === "reviewed" ? (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-500/[0.06] p-6">
                <p className="text-sm font-semibold text-emerald-700">{t("expertDash.alreadyReviewed")}</p>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="verdict-form">
                <h2 className="font-semibold text-slate-900">{t("expertDash.yourVerdict")}</h2>
                <label className="mt-4 block text-sm font-medium text-slate-600">{t("expertDash.level")}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {LEVELS.map((lv) => {
                    const s = statusFor(lv);
                    const on = form.risk_level === lv;
                    return (
                      <button key={lv} data-testid={`verdict-level-${lv}`} onClick={() => setForm((f) => ({ ...f, risk_level: lv }))}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? `${s.bg} ${s.text} ${s.ring} ring-1` : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {t(`common.${lv.toLowerCase()}`)}
                      </button>
                    );
                  })}
                </div>
                <label className="mt-4 block text-sm font-medium text-slate-600">{t("expertDash.summary")}</label>
                <textarea data-testid="verdict-summary" rows={3} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none" />
                <label className="mt-4 block text-sm font-medium text-slate-600">{t("expertDash.advice")}</label>
                <textarea data-testid="verdict-advice" rows={3} value={form.advice} onChange={(e) => setForm((f) => ({ ...f, advice: e.target.value }))}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none" />
                {err && <p className="mt-3 text-sm text-amber-800" data-testid="verdict-error">{err}</p>}
                <button data-testid="verdict-submit" onClick={submit} disabled={busy}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("expertDash.submit")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="mt-6 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
              {["in_review", "reviewed"].map((s) => (
                <button key={s} data-testid={`expert-tab-${s}`} onClick={() => setTab(s)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === s ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:text-slate-900"}`}>
                  {t(`expert.status.${s}`)}
                </button>
              ))}
            </div>

            {data === null ? (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
            ) : data.items.length === 0 ? (
              <div className="mt-8 flex flex-col items-center rounded-3xl border border-slate-200 bg-slate-50 py-16 text-center" data-testid="expert-empty">
                <Inbox className="h-10 w-10 text-slate-600" />
                <p className="mt-4 text-slate-500">{t("expertDash.empty")}</p>
              </div>
            ) : (
              <div className="mt-6 space-y-3" data-testid="expert-queue-list">
                {data.items.map((it) => {
                  const lv = it.ai_risk_level || "LIMITED";
                  const st = statusFor(lv);
                  const Icon = st.icon;
                  return (
                    <button key={it.request_id} data-testid={`expert-queue-item-${it.request_id}`} onClick={() => openItem(it)}
                      className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:bg-slate-50">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${st.bg} ring-1 ${st.ring}`}>
                        <Icon className={`h-5 w-5 ${st.text}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-700">{it.masked_preview || it.input_type}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{it.input_type} · AI: {it.ai_risk_level || "—"}</p>
                      </div>
                      <span className="hidden shrink-0 text-xs text-blue-700 sm:inline">{t("expertDash.open")}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
